import React, { FC, memo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Hidden, IconButton, Grid, Link } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Avatar } from 'stream-chat-react';
import classNames from 'classnames';
import MenuIcon from '@material-ui/icons/Menu';

import { Events } from 'analytics';
import { useContent } from 'hooks';
import { CustomSVG } from 'components';
import { generateLink } from 'Links';
import { ShareDialog } from 'features/sharing';

import useChannelContext from './useChannelContext';

interface IProps {
  onToggleDrawer: () => void;
}

const useStyles = makeStyles(theme => ({
  root: {
    padding: theme.spacing(2),
    backgroundColor: theme.palette.common.white,
    borderTopLeftRadius: 4,
    borderTopRightRadius: 4,
    border: `1px solid ${theme.palette.grey[300]}`,
  },
  name: {
    maxWidth: 700, // magic number to ensure no overflow
  },
  hasShared: {
    backgroundColor: theme.palette.grey[400],
  },
  importantShareButton: {
    padding: `4px ${theme.spacing(2)}px 3px`,
  },
  importantShareIcon: {
    color: theme.palette.common.white,
  },
  menuButton: {
    marginLeft: -12,
  },
}));

const ChannelHeader: FC<IProps> = ({ onToggleDrawer }) => {
  const classes = useStyles();
  const { channel } = useChannelContext();
  const { t } = useTranslation();
  const { content_id, has_requesting_user_shared } = useContent();
  const [open, setOpen] = useState(false);

  const { name, image } = channel.data;
  const buttonClasses = classNames(classes.importantShareButton, {
    [classes.hasShared]: has_requesting_user_shared,
  });

  function handleOpen() {
    setOpen(true);
    analytics.track(Events.ShareInitiated, { content_id });
  }

  function handleClose() {
    setOpen(false);
  }

  function renderMenuButton() {
    return (
      <IconButton onClick={onToggleDrawer}>
        <MenuIcon />
      </IconButton>
    );
  }

  return (
    <div className={classes.root}>
      <Grid container alignItems="center" spacing={2}>
        <Hidden mdUp implementation="css">
          <Grid item>{renderMenuButton()}</Grid>
        </Hidden>
        <Grid item>
          <Avatar image={image} shape="rounded" size={40} />
        </Grid>
        <Grid item xs zeroMinWidth>
          <Link
            display="block"
            variant="subtitle2"
            noWrap
            className={classes.name}
            component={generateLink(`/content/${channel.id}`)}>
            {name}
          </Link>
        </Grid>
        <Grid item>
          <Button
            className={buttonClasses}
            variant="contained"
            color="primary"
            size="small"
            onClick={handleOpen}
            endIcon={<CustomSVG className={classes.importantShareIcon} name="arrow" size={24} />}>
            {t('content:share')}
          </Button>
        </Grid>
      </Grid>
      <ShareDialog onClose={handleClose} open={open} />
    </div>
  );
};

export default memo(ChannelHeader);
